import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { usePatients } from '../contexts/PatientsContext';
import { patientsService } from '../adapters/patients';
import { Patient } from '../types';
import { PatientSearchBar } from './patients/PatientSearchBar';
import { PatientListItem } from './patients/PatientListItem';
import { PatientSkeletonList, EmptyState } from './patients/PatientSkeleton';

const MIN_LEN = 3;

const PatientNavPanel = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { patients, nextCursor, loading, initialLoaded, fetchInitial, loadMore } = usePatients();

  const [searchTerm, setSearchTerm] = React.useState('');
  const [searchResults, setSearchResults] = React.useState<Patient[]>([]);
  const [searchCursor, setSearchCursor] = React.useState<string | null>(null);
  const [searchLoading, setSearchLoading] = React.useState(false);
  const [searchActive, setSearchActive] = React.useState(false);
  const requestIdRef = React.useRef(0);

  React.useEffect(() => {
    if (!initialLoaded) fetchInitial();
  }, [initialLoaded, fetchInitial]);

  const highlightPrefix = React.useCallback((first: string, last: string): React.ReactNode => {
    const prefix = searchTerm.trim().toLowerCase();
    if (!searchActive || prefix.length < MIN_LEN) return <>{first} {last}</>;
    const renderWord = (word: string) => {
      if (!word) return '';
      if (word.toLowerCase().startsWith(prefix)) {
        return (
          <>
            <span className='bg-yellow-200/80 text-gray-900 font-semibold rounded px-0.5'>{word.slice(0, prefix.length)}</span>
            {word.slice(prefix.length)}
          </>
        );
      }
      return word;
    };
    return <>{renderWord(first)} {renderWord(last)}</>;
  }, [searchTerm, searchActive]);

  const performSearch = React.useCallback(async (term: string, cursor: string | null = null) => {
    if (term.length < MIN_LEN) return;
    const myId = ++requestIdRef.current;
    setSearchLoading(true);
    try {
      const data = await patientsService.searchPatients(term, 20, cursor);
      if (requestIdRef.current !== myId) return;
      setSearchResults(prev => cursor ? [...prev, ...(data.items || [])] : (data.items || []));
      setSearchCursor(data.next_cursor || null);
      setSearchActive(true);
    } catch (_) {}
    finally {
      if (requestIdRef.current === myId) setSearchLoading(false);
    }
  }, []);

  React.useEffect(() => {
    if (searchTerm.length < MIN_LEN) {
      setSearchActive(false);
      setSearchResults([]);
      setSearchCursor(null);
      setSearchLoading(false);
      return;
    }
    const h = setTimeout(() => performSearch(searchTerm), 320);
    return () => clearTimeout(h);
  }, [searchTerm, performSearch]);

  const clearSearch = () => {
    setSearchTerm('');
    setSearchActive(false);
    setSearchResults([]);
    setSearchCursor(null);
    setSearchLoading(false);
  };

  const loadMoreRef = React.useRef<HTMLDivElement>(null);
  const searchLoadMoreRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const node = loadMoreRef.current;
    if (!node) return;
    const obs = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && !searchActive && nextCursor && !loading) loadMore();
    }, { threshold: 0.1 });
    obs.observe(node);
    return () => obs.disconnect();
  }, [nextCursor, loading, searchActive, loadMore]);

  React.useEffect(() => {
    const node = searchLoadMoreRef.current;
    if (!node) return;
    const obs = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && searchActive && searchCursor && !searchLoading) {
        performSearch(searchTerm, searchCursor);
      }
    }, { threshold: 0.1 });
    obs.observe(node);
    return () => obs.disconnect();
  }, [searchActive, searchCursor, searchLoading, searchTerm, performSearch]);

  const displayList = searchActive ? searchResults : patients;

  return (
    <div className='flex-1 overflow-hidden flex flex-col min-h-0 mt-4'>
      <PatientSearchBar
        value={searchTerm}
        onChange={setSearchTerm}
        onClear={clearSearch}
        minLen={MIN_LEN}
        loading={searchLoading}
      />
      <div className='flex-1 overflow-y-auto px-2 space-y-0.5' aria-label='Patient list'>
        {searchActive && (
          <div className='px-3 pt-1 pb-1 text-[10px] uppercase tracking-wide text-gray-500 sticky top-0 bg-white/95 backdrop-blur z-10'>
            Search Results
          </div>
        )}
        {displayList.map(p => (
          <PatientListItem
            key={p.patient_id}
            patient={p}
            active={location.pathname === `/patients/${p.patient_id}`}
            onSelect={pt => navigate(`/patients/${pt.patient_id}`)}
            highlightName={(fn, ln) => highlightPrefix(fn, ln)}
            tags={p.tags}
          />
        ))}
        {(searchLoading || (loading && !initialLoaded)) && (
          <PatientSkeletonList count={searchActive ? 4 : 6} />
        )}
        {!searchLoading && searchActive && searchResults.length === 0 && searchTerm.length >= MIN_LEN && (
          <EmptyState
            title='No matches'
            subtitle={`No patients found for "${searchTerm}"`}
            action={
              <button onClick={clearSearch} className='text-brand-navy text-xs font-medium hover:underline'>
                Clear search
              </button>
            }
          />
        )}
        {!searchActive && !loading && patients.length === 0 && initialLoaded && (
          <EmptyState title='No patients yet' subtitle='Add your first patient to get started.' />
        )}
        {!searchActive && nextCursor && <div ref={loadMoreRef} className='h-6' aria-hidden='true' />}
        {searchActive && searchCursor && <div ref={searchLoadMoreRef} className='h-6' aria-hidden='true' />}
      </div>
    </div>
  );
};

export default PatientNavPanel;
